import { Fragment, useState, type ReactNode } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import RuleIcon from '@mui/icons-material/RuleOutlined';
import AudioIcon from '@mui/icons-material/GraphicEqOutlined';
import VideoIcon from '@mui/icons-material/MovieOutlined';
import ReviewIcon from '@mui/icons-material/FactCheckOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from '../../i18n';
import { format } from '../../i18n/I18nProvider';
import {
  parseArgs,
  type AgentOption,
  type Attachment,
  type EventContent,
  type Proposal,
  type StoredMessage,
} from '../../lib/agent';
import { ProposalCard } from './ProposalCard';
import { AgentErrorNotice } from './AgentErrorNotice';

// **bold** and line breaks — the agent is told not to use other markdown.
function RichText({ text }: { text: string }) {
  return (
    <>
      {text.split('\n').map((line, i) => (
        <Fragment key={i}>
          {i > 0 && <br />}
          {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
            part.startsWith('**') && part.endsWith('**') ? <strong key={j}>{part.slice(2, -2)}</strong> : part
          )}
        </Fragment>
      ))}
    </>
  );
}

function AttachmentTile({ a }: { a: Attachment }) {
  if (a.kind === 'image') {
    return (
      <Box
        component="img"
        src={a.url}
        alt={a.name ?? ''}
        // A reference sheet is shown whole; cropping would hide panels.
        sx={{
          height: a.sheet ? 160 : 120,
          maxWidth: a.sheet ? 320 : 200,
          objectFit: a.sheet ? 'contain' : 'cover',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
    );
  }
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: 'action.selected' }}>
      {a.kind === 'audio' ? <AudioIcon fontSize="small" /> : <VideoIcon fontSize="small" />}
      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
        {a.name ?? a.kind}
      </Typography>
    </Stack>
  );
}

function Bubble({ side, children }: { side: 'user' | 'agent'; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: side === 'user' ? 'flex-end' : 'flex-start' }}>
      <Box
        sx={{
          maxWidth: '85%',
          px: side === 'user' ? 1.75 : 0,
          py: side === 'user' ? 1.1 : 0.25,
          borderRadius: 2.5,
          bgcolor: side === 'user' ? 'action.selected' : 'transparent',
          fontSize: 14.5,
          lineHeight: 1.6,
          wordBreak: 'break-word',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function Reasoning({ text, live, status }: { text: string; live?: boolean; status?: string }) {
  const t = useTranslation().videoAgent;
  const [open, setOpen] = useState(false);
  if (!text && !live) return null;
  return (
    <Box>
      <Button
        size="small"
        onClick={() => setOpen((v) => !v)}
        startIcon={live ? <CircularProgress size={12} /> : undefined}
        sx={{ color: 'text.secondary', px: 0.5, minWidth: 0, fontWeight: 500 }}
      >
        {live ? (status ?? t.thinking) : t.showThinking}
      </Button>
      <Collapse in={open}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', pl: 1.5, borderLeft: '2px solid', borderColor: 'divider', maxHeight: 240, overflow: 'auto', fontSize: 12.5 }}
        >
          {text}
        </Typography>
      </Collapse>
    </Box>
  );
}

const ACTION_ICON: Record<string, ReactNode> = {
  upload_image: <UploadIcon fontSize="small" />,
  upload_audio: <UploadIcon fontSize="small" />,
  upload_video: <UploadIcon fontSize="small" />,
  type: <EditNoteIcon fontSize="small" />,
};

function Options({
  options,
  active,
  chosen,
  onPick,
}: {
  options: AgentOption[];
  active: boolean;
  chosen?: string;
  onPick: (o: AgentOption, index: number) => void;
}) {
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {options.map((o, i) => {
        const picked = chosen === String(i);
        return (
          <ButtonBase
            key={i}
            disabled={!active}
            onClick={() => onPick(o, i)}
            sx={{
              textAlign: 'left',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: picked ? 'primary.main' : 'divider',
              bgcolor: picked ? 'rgba(124, 111, 240, 0.14)' : 'background.paper',
              opacity: active || picked ? 1 : 0.45,
              transition: 'border-color .15s, background-color .15s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              maxWidth: 320,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {o.action && ACTION_ICON[o.action] && <Box sx={{ color: 'primary.light', mt: '1px' }}>{ACTION_ICON[o.action]}</Box>}
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.label}</Typography>
                {o.description && (
                  <Typography variant="body2" sx={{ fontSize: 12 }}>
                    {o.description}
                  </Typography>
                )}
              </Box>
            </Stack>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}

type PlanResult = { min_seconds_needed?: number; recommended_duration?: number };

export type ThreadHandlers = {
  onPick: (callId: string, o: AgentOption, index: number) => void;
  onConfirm: (callId: string, edits: { prompt?: string; duration?: number; tier?: string }) => Promise<void>;
  onCancel: (callId: string) => Promise<void>;
  onUseImage: (a: Attachment, action: 'animate' | 'edit') => void;
  // Re-run the last turn after it failed (turn_failed is the latest turn).
  onRetryTurn?: () => void;
  // Ask the agent to revise a proposal the content check refused.
  onRevise?: (callId: string) => void;
};

// A step of an API run that is not an agent message (read-only run threads).
function RunStatusLine({ event }: { event: Extract<EventContent, { type: 'run_event' }> }) {
  if (event.data?.header) {
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: 1, color: 'text.primary' }}>
        <VideoIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{event.message}</Typography>
        <Box sx={{ flex: 1, borderTop: 1, borderColor: 'divider' }} />
      </Stack>
    );
  }
  const Icon = event.kind === 'review' ? ReviewIcon : event.kind === 'error' ? ErrorIcon : InfoIcon;
  return (
    <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ color: event.kind === 'error' ? 'error.main' : 'text.secondary' }}>
      <Icon sx={{ fontSize: 16, mt: '2px' }} />
      <Typography variant="body2" sx={{ fontSize: 12.5, lineHeight: 1.5 }}>
        {event.message}
      </Typography>
    </Stack>
  );
}

export function AgentThread({
  messages,
  busy,
  draft,
  handlers,
}: {
  messages: StoredMessage[];
  busy: boolean;
  draft: { text: string; reasoning: string; status?: string } | null;
  handlers: ThreadHandlers;
}) {
  const t = useTranslation().videoAgent;

  const results = new Map<string, unknown>();
  const events = new Map<string, EventContent>();
  const choices = new Map<string, string>();
  let lastUserSeq = 0;
  for (const m of messages) {
    if (m.role === 'tool') results.set(m.content.call_id, m.content.result);
    // job_finished is for the model; the job card tracks the job itself.
    if (m.role === 'event' && 'call_id' in m.content && m.content.type !== 'job_finished') events.set(m.content.call_id, m.content);
    if (m.role === 'user') {
      lastUserSeq = m.seq;
      if (m.content.choice) choices.set(m.content.choice.call_id, m.content.choice.option_id);
    }
  }

  // Only a failure that is still the latest turn is shown (with Retry); once
  // anything was said after it, it is history the retry already replaced.
  let lastTurnSeq = 0;
  let lastFailedSeq = 0;
  for (const m of messages) {
    if (m.role !== 'event') lastTurnSeq = m.seq;
    else if (m.content.type === 'turn_failed') lastFailedSeq = m.seq;
  }

  return (
    <Stack spacing={2.25}>
      {messages.map((m) => {
        if (m.role === 'user') {
          return (
            <Stack key={m.id} spacing={1} alignItems="flex-end">
              {!!m.content.attachments?.length && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                  {m.content.attachments.map((a) => (
                    <AttachmentTile key={a.r2_key} a={a} />
                  ))}
                </Stack>
              )}
              {m.content.text && (
                <Bubble side="user">
                  <RichText text={m.content.text} />
                </Bubble>
              )}
            </Stack>
          );
        }
        if (m.role === 'assistant') {
          const calls = m.content.tool_calls ?? [];
          return (
            <Stack key={m.id} spacing={1.25}>
              {m.content.reasoning && <Reasoning text={m.content.reasoning} />}
              {m.content.text && m.content.text !== '…' && (
                <Bubble side="agent">
                  <RichText text={m.content.text} />
                </Bubble>
              )}
              {calls.map((c) => {
                const args = parseArgs(c);
                const result = results.get(c.id) as { error?: string; status?: string; proposal?: Proposal } | PlanResult | undefined;
                if (c.name === 'offer_options') {
                  const options = (Array.isArray(args.options) ? args.options : []) as AgentOption[];
                  const message = typeof args.message === 'string' ? args.message : '';
                  // Only the newest options (nothing typed since) are clickable.
                  const active = !busy && m.seq > lastUserSeq;
                  return (
                    <Stack key={c.id} spacing={1}>
                      {message && (
                        <Bubble side="agent">
                          <RichText text={message} />
                        </Bubble>
                      )}
                      <Options options={options} active={active} chosen={choices.get(c.id)} onPick={(o, i) => handlers.onPick(c.id, o, i)} />
                    </Stack>
                  );
                }
                if ((c.name === 'propose_video' || c.name === 'propose_image') && result && 'proposal' in result && result.proposal) {
                  return (
                    <ProposalCard
                      key={c.id}
                      proposal={result.proposal}
                      event={events.get(c.id)}
                      disabled={busy}
                      onConfirm={(edits) => handlers.onConfirm(c.id, edits)}
                      onCancel={() => handlers.onCancel(c.id)}
                      onUseImage={handlers.onUseImage}
                      onRevise={handlers.onRevise ? () => handlers.onRevise!(c.id) : undefined}
                    />
                  );
                }
                if (c.name === 'make_reference_sheet' && result && 'attachment' in result) {
                  const sheet = (result as { attachment: Attachment }).attachment;
                  return (
                    <Stack key={c.id} spacing={0.5} alignItems="flex-start">
                      <AttachmentTile a={sheet} />
                      <Typography variant="body2" sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        {format(t.referenceSheet, { subject: sheet.sheet?.subject ?? '' })}
                      </Typography>
                    </Stack>
                  );
                }
                if (c.name === 'check_video_plan' && result && 'recommended_duration' in result) {
                  return (
                    <Stack key={c.id} direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                      <RuleIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" sx={{ fontSize: 12.5 }}>
                        {format(t.planCheck, { need: result.min_seconds_needed ?? '?', rec: result.recommended_duration ?? '?' })}
                      </Typography>
                    </Stack>
                  );
                }
                return null;
              })}
            </Stack>
          );
        }
        if (m.role === 'event' && m.content.type === 'run_event') return <RunStatusLine key={m.id} event={m.content} />;
        // proposal_failed is shown on its card, with Retry.
        if (m.role === 'event' && m.content.type === 'turn_failed' && m.seq === lastFailedSeq && m.seq > lastTurnSeq) {
          return (
            <AgentErrorNotice
              key={m.id}
              context="turn"
              error={{ raw: m.content.error }}
              onRetry={handlers.onRetryTurn}
              retrying={busy}
            />
          );
        }
        return null;
      })}

      {draft && (
        <Stack spacing={1}>
          <Reasoning text={draft.reasoning} live={!draft.text || !!draft.status} status={draft.status} />
          {draft.text && (
            <Bubble side="agent">
              <RichText text={draft.text} />
            </Bubble>
          )}
        </Stack>
      )}
    </Stack>
  );
}
